import React, { useState } from 'react'
import { LoaderBar, PageHeader } from 'components'
import { CustomerAddEditModal, CustomerAdditionalInfoModal, CustomerList } from 'page-components/customer'
import { CustomerUserAssignmentModal } from 'page-components/customer/CustomerUserAssignmentModal'
import { CustomerNotificationModal } from 'page-components/customer/CustomerNotificationModal'
import { GetCustomerList } from 'hooks'
import { CustomerService, canManageDepartmentsByRole } from 'utility'
import { Input } from 'semantic-ui-react'
import { DesktopMode, MobileMode } from 'layout'
import { useAuth } from 'context'

export const CustomerListScreen = () => {
    const { data, setData, loading, filter, pageChanged, totalCount, refresh } = GetCustomerList()
    const { user } = useAuth()
    const companyId = user?.CompanyId || user?.Companies?.[0]?.CompanyId
    const [processing, setProcessing] = useState('')
    const [modalData, setModalData] = useState({})
    const [additionalInfoList, setAdditionalInfoList] = useState([])
    const [additionalInfoModalOpen, setAdditionalInfoModalOpen] = useState(false)
    const [selectedCustomerForAdditionalInfo, setSelectedCustomerForAdditionalInfo] = useState(null)
    const [additionalInfoModalMode, setAdditionalInfoModalMode] = useState('add')
    const [search, setSearch] = useState('')
    const [filteredData, setFilteredData] = useState(data)
    const [showUserAssignmentModal, setShowUserAssignmentModal] = useState(false)
    const [selectedCustomerForAssignment, setSelectedCustomerForAssignment] = useState(null)
    const [showNotificationModal, setShowNotificationModal] = useState(false)
    const [selectedCustomerForNotification, setSelectedCustomerForNotification] = useState(null)

    // Update filteredData when data or search changes
    React.useEffect(() => {
        if (!search.trim()) {
            setFilteredData(data)
        } else {
            const searchValue = search.toLowerCase()
            setFilteredData(
                data.filter(
                    ele =>
                        (ele.customer_name && ele.customer_name.toLowerCase().includes(searchValue)) ||
                        (ele.Email && ele.Email.toLowerCase().includes(searchValue)),
                ),
            )
        }
    }, [data, search])

    const remove = async (id) => {
        setProcessing("Processing")
        try {
            await CustomerService.remove(id)
            setData(data.filter(ele => ele.unique_id !== id))
        } catch (error) {
            console.log(error)
        } finally {
            setProcessing("")
        }
    }

    const onClose = async (formdata) => {
        try {
            if (formdata) {
                let response
                if (formdata?.unique_id) {
                    setProcessing("Saving")
                    response = await CustomerService.update(formdata)
                } else {
                    setProcessing("Saving")
                    response = await CustomerService.add(formdata)
                }

                // Check if IsUserAssignmentEnabled is true
                const customerId = formdata?.unique_id || response?.unique_id || response?.CustomerId
                const isUserAssignmentEnabled = response?.IsUserAssignmentEnabled === true ||
                                                formdata?.IsUserAssignmentEnabled === true

                if (isUserAssignmentEnabled && customerId && companyId) {
                    // Show user assignment modal after successful save
                    setSelectedCustomerForAssignment({ unique_id: customerId })
                    setShowUserAssignmentModal(true)
                } else {
                    refresh()
                }
            }
        } catch (error) {
            console.log(error)
        } finally {
            setProcessing("")
            setModalData({})
            setSelectedCustomerForAdditionalInfo(null)
        }
    }

    const handleUserAssignmentModalClose = () => {
        setShowUserAssignmentModal(false)
        setSelectedCustomerForAssignment(null)
        refresh() // Refresh the customer list
    }

    const editCustomer = (data) => {
        setModalData(data)
    }

    // Handler for editing additional info
    const onEditAdditionalInfo = async (customer) => {
        setProcessing('Loading Additional Info...')
        try {
            const result = await CustomerService.getCustomerAdditionalInfo(customer.unique_id)
            setAdditionalInfoList(Array.isArray(result) && result.length > 0 ? result : [{
                UniqueId: 0,
                CustomerId: customer.unique_id,
                FieldName: '',
                FieldType: '',
                FieldValue: '',
                ShowInReport: false,
            }])
            setAdditionalInfoModalMode('edit')
            setAdditionalInfoModalOpen(true)
            setSelectedCustomerForAdditionalInfo(customer)
        } catch (e) {
            setAdditionalInfoList([])
            setAdditionalInfoModalMode('edit')
            setAdditionalInfoModalOpen(true)
            setSelectedCustomerForAdditionalInfo(customer)
        } finally {
            setProcessing('')
        }
    }

    // Handler for adding additional info
    const onAddAdditionalInfo = (customer = null) => {
        setAdditionalInfoList([
            {
                UniqueId: 0,
                CustomerId: customer && typeof customer.unique_id === 'number' ? customer.unique_id : null,
                FieldName: '',
                FieldType: '',
                FieldValue: '',
                ShowInReport: false,
            },
        ])
        setSelectedCustomerForAdditionalInfo(customer && typeof customer.unique_id === 'number' ? customer : null)
        setAdditionalInfoModalMode('add')
        setAdditionalInfoModalOpen(true)
    }

    // Handler for setting auto email
    const onSetAutoEmail = (customer) => {
        setSelectedCustomerForNotification(customer)
        setShowNotificationModal(true)
    }

    const handleNotificationModalClose = (shouldRefresh) => {
        setShowNotificationModal(false)
        setSelectedCustomerForNotification(null)
        if (shouldRefresh) {
            refresh()
        }
    }

    return (
        <>
            {/* Desktop Layout */}
            <DesktopMode>
                <div className="row mb-3">
                    <div className="col-12">
                        <PageHeader header="Customer List" />
                    </div>
                </div>
            </DesktopMode>

            {/* Mobile Layout */}
            <MobileMode>
                <PageHeader header="Customer List" />
            </MobileMode>

            {canManageDepartmentsByRole(user) && (
                <div className="col-12">
                    <CustomerAddEditModal
                        onClose={onClose}
                        data={modalData}
                        loading={processing}
                        additionalInfoList={additionalInfoList}
                        setAdditionalInfoList={setAdditionalInfoList}
                    />
                </div>
            )}
            <div className="col-12">
                <CustomerAdditionalInfoModal
                    open={additionalInfoModalOpen}
                    onClose={info => {
                        setAdditionalInfoModalOpen(false);
                        if (selectedCustomerForAdditionalInfo && typeof selectedCustomerForAdditionalInfo.unique_id === 'number') {
                            refresh();
                        }
                        if (info && !selectedCustomerForAdditionalInfo) {
                            setAdditionalInfoList(list => [...list, info]);
                        }
                    }}
                    customer={selectedCustomerForAdditionalInfo}
                    additionalInfoList={additionalInfoList}
                    customerList={data}
                    mode={additionalInfoModalMode}
                />
            </div>
            {selectedCustomerForAssignment && companyId && (
                <CustomerUserAssignmentModal
                    open={showUserAssignmentModal}
                    onClose={handleUserAssignmentModalClose}
                    customerId={selectedCustomerForAssignment.unique_id}
                    companyId={companyId}
                />
            )}
            {selectedCustomerForNotification && (
                <CustomerNotificationModal
                    open={showNotificationModal}
                    onClose={handleNotificationModalClose}
                    customer={selectedCustomerForNotification}
                />
            )}
            <div className="my-3">
                <Input
                    placeholder="Search by name or email"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ marginBottom: 16, width: 300 }}
                    icon={{ name: 'search', circular: true, link: true }}
                />
                {(processing || loading) && <LoaderBar content={processing || "Loading"} />}
                <CustomerList
                    data={filteredData}
                    pageChanged={pageChanged}
                    totalCount={totalCount}
                    filter={filter}
                    remove={remove}
                    onEdit={editCustomer}
                    onEditAdditionalInfo={onEditAdditionalInfo}
                    onAddAdditionalInfo={onAddAdditionalInfo}
                    onSetAutoEmail={onSetAutoEmail}
                />
            </div>
        </>
    )
}